import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Button, Box } from '@material-ui/core';

import { AppState } from '../../redux';
import { OrderSummary } from '../../components/OrderSummary';
import { removeDirectSaleProductAction } from '../../redux/actions/directSaleCart.actions';
import { calcOrderProductsSubtotal } from '../../helpers/utils';
import { getAuthenticationState } from '../../redux/selectors';
import { ShopMode } from '../../redux/types';

const useStyles = makeStyles((theme) => ({
  infoBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  notice: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
}));

const DirectSaleCart: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { items } = useSelector((state: AppState) => state.directSaleCart);
  const { user } = useSelector(getAuthenticationState);

  const handleRemoveProduct = (productOrderId: number) => {
    dispatch(removeDirectSaleProductAction(productOrderId));
    enqueueSnackbar('Prodotto rimosso dal carrello', { variant: 'info' });
  };

  const handleCheckout = () => {
    if (!user) {
      navigate('/signin', { state: { from: '/checkout' } });
      return;
    }
    navigate('/checkout');
  };

  const { subTotal } = calcOrderProductsSubtotal(items, false, undefined, ShopMode.DirectSale);

  if (items.length === 0) {
    return (
      <Container>
        <Typography variant="h3" gutterBottom color="primary">
          Carrello Vendita Online
        </Typography>
        <Typography variant="body1" paragraph>
          Il tuo carrello è vuoto
        </Typography>
        <Button variant="contained" color="primary" component={RouterLink} to="/ordina">
          Vai ai prodotti
        </Button>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h3" gutterBottom color="primary">
        Carrello Vendita Online
      </Typography>
      <Typography variant="body1" gutterBottom>
        Controlla i prodotti nel tuo carrello. Potrai completare il pagamento in modo sicuro con Stripe dopo aver
        inserito l'indirizzo di spedizione.
      </Typography>

      <OrderSummary
        orderProducts={items}
        isUserLogged={!!user}
        editable
        removeProductAction={handleRemoveProduct}
        shopMode={ShopMode.DirectSale}
      />

      <Box className={classes.infoBox}>
        <Typography variant="h5">Subtotale: €{subTotal.toFixed(2)}</Typography>
      </Box>

      <Box className={classes.notice}>
        <Typography variant="caption" display="block">
          * Il pagamento verrà elaborato in modo sicuro da Stripe
        </Typography>
      </Box>

      <Box className={classes.buttonBox}>
        <Button variant="outlined" component={RouterLink} to="/ordina" color="primary">
          Continua acquisti
        </Button>

        <Button variant="contained" color="primary" size="large" onClick={handleCheckout} disabled={items.length === 0}>
          Procedi al pagamento
        </Button>
      </Box>
    </Container>
  );
};

export default DirectSaleCart;
